import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostBinding,
  HostListener,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { merge, chunk, max, isEqual } from 'lodash';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  ColorService,
  ColumnOrderEntry,
  DEFAULT_EXCLUDE_FALSY_TOOGLE,
  Dimension,
  DimensionProperty,
  Metric,
  MetricValue,
  Result,
  BenchmarkService,
  DatesService,
  Query,
  DateRangeValue,
  DynamicDateRange,
  Series,
  Row,
  MessageService,
  EMPTY_FALSY_ERROR,
  TrackingService,
  WidgetQueueProxyService,
  WrapMiddleService,
} from '@hub-components';
import { MetricFormat } from 'app/apps/studio/app/models/dashboard.model';
import { BenchmarkSelection } from 'app/hub-components/components/benchmarks/benchmarks.component';
import { DEFAULT_TABLE_TEXT_FONT_SIZE, DEFAULT_RESULTS_PER_PAGE, DEFAULT_COMPARE_TO, DEFAULT_SHOW_COLUMN_GRAND_TOTALS, DEFAULT_SHOW_ROW_GRAND_TOTALS, DEFAULT_SHOW_COLUMN_ALL_PAGES_TOTALS } from '../../../../constants';
import { MetricTotal } from '../../interfaces/metric-total.interface';
import { WidgetStyle, WidgetStyleOptions } from '../../interfaces/widget-style.interface';
import { ChartOptions } from '../chart-options';
import { resolveDashboardMetricFormat } from '../widget-metric-format.util';
type MetricComparison = 'green_increase' | 'red_increase';

type CopiedRow = { isHeader: boolean; cells: string[] };
type CopiedTable = { title: string | null; rows: CopiedRow[] };

type TableColumn =
  | { id: string; kind: 'rollup' }
  | { id: string; kind: 'dimension'; dimension: Dimension; dimensionIndex: number }
  | { id: string; kind: 'property'; dimension: Dimension; dimensionIndex: number; property: DimensionProperty; propertyIndex: number }
  | { id: string; kind: 'metric'; metric: Metric; metricIndex: number };

@Component({
  selector: 'table-widget',
  templateUrl: 'table-widget.component.html',
  styleUrls: ['table-widget.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class TableWidgetComponent implements OnChanges, OnInit {
  @HostBinding('style.backgroundColor') backgroundColor: string;

  @Input() result: Result;
  @Input() metrics: Metric[];
  @Input() style: WidgetStyle;
  @Input() chartOptions: ChartOptions = {};
  @Input() dashboardInterval: DynamicDateRange;
  @Input() dashboardMetricFormats: MetricFormat[] = [];
  @Input() widgetId: string;
  @Input() canReorderColumns: boolean = false;

  @Output() columnOrderChanged = new EventEmitter<{ widgetId: string; columnOrder: ColumnOrderEntry[] }>();

  dateFormat = { pattern: 'MMM d, yyyy', timezone: 'UTC' };

  static supportedOptions = [
    {
      bindTo: 'rollup',
      label: 'Sum by',
      type: 'rollupDropdown',
    },
    {
      bindTo: 'transpose',
      label: 'Show dates as',
      type: 'transpose',
    },
    {
      bindTo: 'limit',
      label: 'Number of results',
      type: 'limitDropdown',
    },
    {
      bindTo: 'resultsPerPage',
      label: 'Results per page',
      type: 'resultsPerPageDropdown',
    },
    {
      bindTo: 'dataOptions',
      label: 'Data',
      type: 'dataOptions',
      options: [
        {
          bindTo: 'excludeFalsy',
          label: 'Exclude N/A and 0 values by row',
        }, {
          bindTo: 'showColumnGrandTotals',
        }, {
          bindTo: 'showColumnAllPagesTotals',
        }, {
          bindTo: 'showRowGrandTotals',
        },
      ],
    },
    {
      bindTo: 'heatmap',
      label: 'Heatmap',
      type: 'checkbox',
    },
    {
      bindTo: 'title',
      label: 'Title',
      type: 'textFormat',
      options: [
        {
          bindTo: 'text',
        }, {
          bindTo: 'align',
        }, {
          bindTo: 'size',
        },
      ],
    },
    {
      bindTo: 'footer',
      label: 'Footer',
      type: 'footerCheckbox',
      options: [
        {
          bindTo: 'datasetInfo',
        },
        {
          bindTo: 'dateRange',
        },
        {
          bindTo: 'comparisonDateRange',
        },
      ],
    },
    {
      bindTo: 'tableText',
      label: 'Text',
      type: 'textFormat',
      options: [
        {
          bindTo: 'size',
        },
      ],
    },
    {
      bindTo: 'dateRange',
      label: 'Date range',
      type: 'dateRange',
      options: [
        {
          bindTo: 'enabled',
        }, {
          bindTo: 'value',
        },
      ],
    },
    {
      bindTo: 'compareTo',
      label: 'Compare To',
      type: 'compareToDropdown',
    },
    {
      bindTo: 'compareToDisplay',
      type: 'compareToDisplay',
      options: [
        {
          bindTo: 'separateColumn',
        }, {
          bindTo: 'benchmarksAs',
        }, {
          bindTo: 'groupedHeader',
        },
      ],
    },
    {
      bindTo: 'columnOrder',
      type: 'hidden',
    },
  ];

  static supportedStyle: WidgetStyleOptions[] = [
    {
      bindTo: 'backgroundColor',
      label: 'Background Color',
    },
    {
      bindTo: 'textColor',
      label: 'Text Color',
    },
    {
      bindTo: 'borderWidth',
      label: 'Thickness',
    },
    {
      bindTo: 'borderColor',
      label: 'Color',
    },
    {
      bindTo: 'shadow',
      label: 'Size',
    },
    {
      bindTo: 'shadowColor',
      label: 'Color',
    },
    {
      bindTo: 'shadowOpacity',
      label: 'Opacity',
    },
    {
      bindTo: 'borderRadius',
      label: 'Corner Radius',
    },
  ];

  static supportedRollups = ['day', 'week', 'month', 'quarter', 'year', 'total'];
  textColor: string;
  title: { [key: string]: string | number };
  colorScale: any;
  textSize: number = DEFAULT_TABLE_TEXT_FONT_SIZE;
  footer: any;
  public footerData: any;
  public comparisonDateRange: string;
  public comparisonDatePages: string[][];
  public comparisonDates: string[];

  maxValues: { [key: string]: number };
  uniqueDates: any;


  public pages: any;
  public resultSeries: Series[];
  public uniqueSlices: any;
  public datesAsRowsTotals: MetricTotal[] = [];
  public datesAsRowsTotalsByMetric: { [metricName: string]: MetricTotal } = {};
  public datesAsColumnsColTotals: MetricTotal[] = [];
  public datesAsColumnsColTotalsByMetric: MetricTotal[][] = [];
  public datesAsColumnsRowTotals: MetricTotal[][] = [];
  public datesAsColumnsTotalOfTotals: MetricTotal[] = [];
  public showColumnGrandTotals = DEFAULT_SHOW_COLUMN_GRAND_TOTALS;
  public showRowGrandTotals = DEFAULT_SHOW_ROW_GRAND_TOTALS;
  public showColumnAllPagesTotals = DEFAULT_SHOW_COLUMN_ALL_PAGES_TOTALS;
  public orderedColumns: TableColumn[] = [];
  public orderedMetricColumns: Extract<TableColumn, { kind: 'metric' }>[] = [];
  public firstNonMetricColumnId: string | null = null;
  public spacerBeforeColumnId: string | null = null;
  public draggingId: string | null = null;
  public dropTargetId: string | null = null;
  public dragHandleArmed: boolean = false;
  public updateResult: boolean = true;
  public initialPageNum: number = 0;
  private _uniqueSlices: any;
  private _filteredUniqueSlices: any;
  private _resultData: Series[];
  private _filteredResultData: Series[];
  private _resultSeries: Series[];
  private _pages: any;
  private _filteredPages: any;
  private _excludeFalsy = DEFAULT_EXCLUDE_FALSY_TOOGLE;
  private isFilterEmpty = false;
  private _firstLoad: boolean = true;
  resultsPerPage = DEFAULT_RESULTS_PER_PAGE;
  compareTo: BenchmarkSelection = DEFAULT_COMPARE_TO;
  currentPageNum = 0;


  datasetDisplayName: string;

  benchmarkPages: any;
  benchmarkValuePages: any;
  benchmarkTotals: { [metricName: string]: MetricValue };
  benchmarkPageTotals: { [metricName: string]: number } = {};
  benchmarkDatesLabel: string;
  currentDatesLabel: string;
  separateColumn: boolean = false;
  groupedHeader: boolean = false;
  benchmarksAs: string = 'percentages';
  loadingBenchmarks: boolean = false;

  constructor(
    colorService: ColorService,
    private changeDetectorRef: ChangeDetectorRef,
    private benchmarkService: BenchmarkService,
    private datesService: DatesService,
    private messageService: MessageService,
    private trackingService: TrackingService,
    private widgetQueueProxy: WidgetQueueProxyService,
    private wrapMiddleService: WrapMiddleService,
    private elementRef: ElementRef,
  ) {
    this.colorScale = colorService.getScale(['#e57c76', '#f2b8a3', '#fff3cf', '#aed7ae', '#5abb8c']);
  }

  ngOnInit(): void {
    if (this.chartOptions?.initialPageNum) {
      this.currentPageNum = this.chartOptions.initialPageNum;
      this.initialPageNum = this.chartOptions.initialPageNum;
    }
  }

  @HostListener('copy', ['$event'])
  onCopyText(event: ClipboardEvent): void {
    this.trackingService.track('Table Widget: Copy text', { value: { widgetType: 'table' } });
    this.writeSelectedTableToClipboard(event);
  }

  ngOnChanges(changes) {
    if (this.style) {
      this.textColor = this.style.textColor;
      this.backgroundColor = this.style.backgroundColor;
    }

    if (this.result?.series) {
      this._resultSeries = this.result.series;
      this._resultData = this.getResultsSeriesData(this._resultSeries, false);
      this._filteredResultData = this.getResultsSeriesData(this._resultSeries, true);
      this.isFilterEmpty = this._filteredResultData.length === 0 && this._resultSeries.length !== 0;
      if (this.isFilterEmpty && this._excludeFalsy) {
        this.messageService.info(EMPTY_FALSY_ERROR);
      }
      this.updateResultSeriesData();
    }

    if (this.chartOptions) {
      this.addTracking(changes);

      const compareToDisplay = this.chartOptions.compareToDisplay;
      this.separateColumn = !!compareToDisplay?.separateColumn &&
        !!this.chartOptions.compareTo?.value &&
        this.chartOptions.compareTo.value !== 'none' &&
        this.result?.query?.rollup?.value === 'total' &&
        this.chartOptions.transpose !== 'columns';
      this.benchmarksAs = (this.separateColumn && compareToDisplay?.benchmarksAs?.value) || 'percentages';
      this.groupedHeader = this.separateColumn && !!compareToDisplay?.groupedHeader;

      this.footer = this.chartOptions.footer;
      this.footerData = {
        datasetInfo: this.result?.query?.schema?.displayName,
        dateRange: this.datesService.intervalDateRange(this.dashboardInterval, this.result?.query?.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined),
      };

      if (this.chartOptions.title) {
        this.title = this.chartOptions.title;
        this.title.align = this.chartOptions.title.align || 'center';
      }
      if (this.chartOptions.tableText) {
        this.textSize = this.chartOptions.tableText.size;
      }

      if (this.chartOptions.dataOptions) {
        const excludeFalsyHasChanged = this.hasNewChanges(changes.options?.previousValue?.dataOptions.excludeFalsy, changes.options?.currentValue?.dataOptions.excludeFalsy);
        const showColumnGrandTotalsHasChanged = this.hasNewChanges(changes.options?.previousValue?.dataOptions.showColumnGrandTotals, changes.options?.currentValue?.dataOptions.showColumnGrandTotals);
        const showRowGrandTotalsHasChanged = this.hasNewChanges(changes.options?.previousValue?.dataOptions.showRowGrandTotals, changes.options?.currentValue?.dataOptions.showRowGrandTotals);
        const showColumnAllPagesTotalsHasChanged = this.hasNewChanges(changes.options?.previousValue?.dataOptions.showColumnAllPagesTotals, changes.options?.currentValue?.dataOptions.showColumnAllPagesTotals);

        if (excludeFalsyHasChanged) {
          if (!this._excludeFalsy && this.isFilterEmpty) {
            this.messageService.info(EMPTY_FALSY_ERROR);
          }
          this._excludeFalsy = this.chartOptions.dataOptions.excludeFalsy;
          this.updateSeriesData();
          this.updateSlicesData();
          this.updateResultSeriesData();
          this.resetToFirstPage();
        }

        if (showColumnGrandTotalsHasChanged) {
          this.showColumnGrandTotals = this.chartOptions.dataOptions.showColumnGrandTotals;
          this.resetToFirstPage();
        }

        if (showRowGrandTotalsHasChanged) {
          this.showRowGrandTotals = this.chartOptions.dataOptions.showRowGrandTotals;
          this.resetToFirstPage();
        }

        if (showColumnAllPagesTotalsHasChanged) {
          this.showColumnAllPagesTotals = this.chartOptions.dataOptions.showColumnAllPagesTotals;
          this.resetToFirstPage();
        }
      }

      if (this.chartOptions.resultsPerPage) {
        const resultsPerPageHasChanged = this.hasNewChanges(changes.options?.previousValue?.resultsPerPage, changes.options?.currentValue?.resultsPerPage);
        if (resultsPerPageHasChanged) {
          this.resultsPerPage = this.chartOptions.resultsPerPage.value;
          this.resetToFirstPage();
        }
      }

      if (this.chartOptions.compareTo) {
        this.compareTo = {
          name: this.chartOptions.compareTo.name,
          value: this.chartOptions.compareTo.value,
          dateRange: this.chartOptions.compareTo.dateRange,
          query: this.chartOptions.compareTo.query,
        };
        if (this.result && this.result.query) {
          const dateRange = this.chartOptions.dateRange && this.chartOptions.dateRange.enabled ? this.chartOptions.dateRange.value : this.dashboardInterval;
          const { startDate, endDate } = this.datesService.parseValue(dateRange, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined);
          const dateRangeInvalid = !this.datesService.isValid(startDate, endDate);

          if (!dateRangeInvalid) {
            this.currentDatesLabel = this.dateRangeLabel(startDate, endDate);
            const benchmarkDates = this.getBenchmarkDates(dateRange);
            this.comparisonDateRange = benchmarkDates ? this.datesService.intervalDateRange(benchmarkDates) : undefined;

            if (benchmarkDates) {
              this.loadingBenchmarks = true;
              this.benchmarkDatesLabel = this.dateRangeLabel(benchmarkDates.startDate, benchmarkDates.endDate);
              const benchmarkBase = this.result.query.clone();
              benchmarkBase.interval = this.intervalFromDates(benchmarkDates);
              benchmarkBase.auth = this.chartOptions.compareTo.query && this.chartOptions.compareTo.query['auth'];
              this.chartOptions.compareTo.query = merge({}, benchmarkBase.asJson(), { auth: benchmarkBase.auth });
              const benchmarkResults = this.runQuery(benchmarkBase);
              this.updateBenchmarkPages(benchmarkResults);
            } else {
              this.comparisonDates = undefined;
              this.comparisonDatePages = undefined;
            }
          }
        }
      }
    }

    if (this.result?.rows?.length && this.chartOptions?.heatmap) {
      this.maxValues = {};
      for (const index in this.result.metrics) {
        const metricName = this.result.rows[0].metrics[index].name;
        const values = this.result.rows.map(row => row.metrics[index].value);
        const maxValue = max(values.filter(v => v !== null));
        this.maxValues[metricName] = maxValue;
      }
    }

    if (this.result) {
      this.datasetDisplayName = <string> this.result.query.schema.displayName;
      const transposeHasChanged = this.hasNewChanges(changes.options?.previousValue?.transpose, changes.options?.currentValue?.transpose);
      if (transposeHasChanged && !this._firstLoad) {
        this.resetToFirstPage();
      }
      this.chartOptions.transpose = this.chartOptions.transpose || 'rows';
      const uniqueDateRows = this.result.rows.slice(0, this.result.timestamps.length);
      this.uniqueDates = uniqueDateRows.map(row => row.date);

      if (this.result?.query?.rollup?.value === 'month') {
        this.dateFormat = { pattern: 'MMM yyyy', timezone: 'UTC' };
      } else if (this.result?.query?.rollup?.value === 'year') {
        this.dateFormat = { pattern: 'yyyy', timezone: 'UTC' };
      } else {
        this.dateFormat = { pattern: 'MMM d, yyyy', timezone: 'UTC' };
      }

      this.buildOrderedColumns();
    }

    if (this.result?.rows) {
      const resultHasChanged = this.hasNewChanges(changes.result?.previousValue, changes.result?.currentValue);
      if (resultHasChanged || this.updateResult) {
        this._pages = chunk(this.getRowsData(this.result.rows, false), this.resultsPerPage).map(group => group.filter(item => item !== null));
        this._filteredPages = chunk(this.getRowsData(this.result.rows, true), this.resultsPerPage).map(group => group.filter(item => item !== null));
        this._uniqueSlices = this.getSlicesData(false);
        this._filteredUniqueSlices = this.getSlicesData(true);
        this.updateSeriesData();
        this.updateSlicesData();
        this.updateResultSeriesData();

        if (this.showColumnGrandTotals || this.showRowGrandTotals) {
          this.calculateGrandTotals();
        }

        this.updateResult = false;
        if (!this._firstLoad) {
          this.updateCurrentPageNum(0);
        }
        this._firstLoad = false;
      }
    }

    if (changes.dashboardMetricFormats && this.result?.rows?.length) {
      if (this.showColumnGrandTotals || this.showRowGrandTotals) {
        this.calculateGrandTotals();
      }
    }
  }

  public getMetricFormat(metricName: string, baseFormat: any): any {
    return resolveDashboardMetricFormat(metricName, baseFormat, this.result?.query?.dataset, this.dashboardMetricFormats);
  }

  public isWrapped(text: string): boolean {
    return this.wrapMiddleService.wrapString(text).includes('\n');
  }

  public armDrag(): void {
    this.dragHandleArmed = true;
    this.changeDetectorRef.markForCheck();
  }

  @HostListener('document:mouseup')
  public disarmDrag(): void {
    if (this.dragHandleArmed) {
      this.dragHandleArmed = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  public onDragStart(event: DragEvent, column: TableColumn): void {
    if (!this.canReorderColumns || !this.dragHandleArmed) {
      event.preventDefault();
      return;
    }
    this.draggingId = column.id;
    event.dataTransfer?.setData('text/plain', column.id);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  public onDragOver(event: DragEvent, column: TableColumn): void {
    if (this.draggingId && this.draggingId !== column.id) {
      event.preventDefault();
      this.dropTargetId = column.id;
      this.changeDetectorRef.markForCheck();
    }
  }

  public onDragLeave(column: TableColumn): void {
    if (this.dropTargetId === column.id) {
      this.dropTargetId = null;
      this.changeDetectorRef.markForCheck();
    }
  }

  public onDrop(event: DragEvent, target: TableColumn): void {
    event.preventDefault();
    const sourceId = this.draggingId;
    this.clearDragState();
    if (sourceId && sourceId !== target.id) {
      this.reorderColumn(sourceId, target.id);
    }
  }

  public onDragEnd(): void {
    this.clearDragState();
  }

  private clearDragState(): void {
    this.draggingId = null;
    this.dropTargetId = null;
    this.dragHandleArmed = false;
    this.changeDetectorRef.markForCheck();
  }

  private reorderColumn(sourceId: string, targetId: string): void {
    const src = this.orderedColumns.findIndex(c => c.id === sourceId);
    const dst = this.orderedColumns.findIndex(c => c.id === targetId);
    if (src < 0 || dst < 0) {
      return;
    }

    const next = [...this.orderedColumns];
    const [moved] = next.splice(src, 1);
    next.splice(dst, 0, moved);

    this.orderedColumns = next;
    this.updateDerivedFields();

    const columnOrder = this.isCanonicalOrder(next)
      ? []
      : next.map(col => TableWidgetComponent.toOrderEntry(col));
    this.chartOptions.columnOrder = columnOrder;

    this.trackingService.track('Table Widget: Reorder column', { value: { widgetType: 'table' } });
    this.columnOrderChanged.emit({ widgetId: this.widgetId, columnOrder });
    this.changeDetectorRef.markForCheck();
  }

  private buildOrderedColumns(): void {
    if (!this.result) {
      return;
    }

    const canonical = this.buildCanonicalColumns();

    let ordered: TableColumn[];
    if (this.chartOptions?.columnOrder?.length) {
      ordered = [];
      const remaining = [...canonical];
      for (const entry of this.chartOptions.columnOrder) {
        const idx = remaining.findIndex(c => TableWidgetComponent.entryMatchesColumn(entry, c));
        if (idx >= 0) {
          ordered.push(remaining.splice(idx, 1)[0]);
        }
      }
      ordered = ordered.concat(remaining);
    } else {
      ordered = canonical;
    }

    this.orderedColumns = ordered;
    this.updateDerivedFields();
  }

  private updateDerivedFields(): void {
    this.orderedMetricColumns = this.orderedColumns.filter(
      (col): col is Extract<TableColumn, { kind: 'metric' }> => col.kind === 'metric',
    );
    this.firstNonMetricColumnId = this.orderedColumns.find(col => col.kind !== 'metric')?.id ?? null;

    const firstMetricIndex = this.orderedColumns.findIndex(col => col.kind === 'metric');
    this.spacerBeforeColumnId = firstMetricIndex > 0 ? this.orderedColumns[firstMetricIndex].id : null;
  }

  private buildCanonicalColumns(): TableColumn[] {
    const cols: TableColumn[] = [];

    if (this.chartOptions?.transpose !== 'columns' && this.result.query?.rollup?.value !== 'total') {
      cols.push({ id: 'rollup', kind: 'rollup' });
    }

    this.result.dimensions.forEach((dimension, dimensionIndex) => {
      cols.push({ id: `dim:${dimension.name}`, kind: 'dimension', dimension, dimensionIndex });

      const sliceOrder: string[] = this.result.query?.dimensionProperties?.[dimension.name] ?? [];
      (dimension.properties || []).forEach((property, enumIndex) => {
        const fromSlice = sliceOrder.indexOf(property.name);
        cols.push({
          id: `prop:${dimension.name}.${property.name}`,
          kind: 'property',
          dimension,
          dimensionIndex,
          property,
          propertyIndex: fromSlice >= 0 ? fromSlice : enumIndex,
        });
      });
    });

    this.result.metrics.forEach((metric, metricIndex) => {
      cols.push({ id: `metric:${metric.name}`, kind: 'metric', metric, metricIndex });
    });

    return cols;
  }

  private isCanonicalOrder(columns: TableColumn[]): boolean {
    const canonical = this.buildCanonicalColumns();
    return columns.length === canonical.length
      && columns.every((col, index) => col.id === canonical[index].id);
  }

  private static toOrderEntry(col: TableColumn): ColumnOrderEntry {
    switch (col.kind) {
      case 'rollup':    return { type: 'rollup' };
      case 'dimension': return { type: 'dimension', dimension: col.dimension.name };
      case 'property':  return { type: 'property', dimension: col.dimension.name, property: col.property.name };
      case 'metric':    return { type: 'metric', metric: col.metric.name };
    }
  }

  private static entryMatchesColumn(entry: ColumnOrderEntry, col: TableColumn): boolean {
    switch (entry.type) {
      case 'rollup':    return col.kind === 'rollup';
      case 'dimension': return col.kind === 'dimension' && col.dimension.name === entry.dimension;
      case 'property':  return col.kind === 'property' && col.dimension.name === entry.dimension && col.property.name === entry.property;
      case 'metric':    return col.kind === 'metric' && col.metric.name === entry.metric;
    }
  }

  private calculateGrandTotals(): void {
    this.datesAsColumnsRowTotals = [];
    this.datesAsColumnsColTotals = [];
    const datesAsRowsTotals = {};
    const startIndex = this.currentPageNum * this.resultsPerPage;
    const endIndex = startIndex + this.resultsPerPage;

    if (this.chartOptions?.transpose !== 'columns' && this.result?.query?.rollup?.value !== 'total') {
      const pageRows = this.pages[this.currentPageNum] || [];
      pageRows.forEach(row => {
        row.metrics.forEach(metric => {
          const customMetricFormat = this.getMetricFormat(metric.name, metric.format);
          const baseFormat = customMetricFormat?.['format'] || customMetricFormat;
          const hide = baseFormat === 'percent' || baseFormat === 'date';
          if (!datesAsRowsTotals[metric.name]) {
            datesAsRowsTotals[metric.name] = { value: 0, format: customMetricFormat, hide: hide };
          }
          datesAsRowsTotals[metric.name].value += metric.value || 0;
        });
      });
      this.datesAsRowsTotals = Object.values(datesAsRowsTotals || {});
    } else {
      const pageResultSeries = this.resultSeries.slice(startIndex, endIndex);
      pageResultSeries.forEach(serie => {
        let rowTotals = [];
        let index = 0;
        serie.metrics.forEach(metric => {
          const customMetricFormat = this.getMetricFormat(metric.name, metric.format);
          const baseFormat = customMetricFormat?.['format'] || customMetricFormat;
          const hide = baseFormat === 'percent' || baseFormat === 'date';
          datesAsRowsTotals[metric.name] = { value: (datesAsRowsTotals[metric.name] && datesAsRowsTotals[metric.name].value || 0) + metric.aggregate, format: customMetricFormat, hide: hide };
          rowTotals.push({ value: metric.aggregate, format: customMetricFormat, hide: hide });
          metric.values.forEach(value => {
            if (this.datesAsColumnsColTotals[index]) {
              this.datesAsColumnsColTotals[index]['value'] += Number(value) || 0;
            } else {
              this.datesAsColumnsColTotals[index] = { value: Number(value) || 0, format: customMetricFormat, hide: hide };
            }
            index++;
          });
        });
        this.datesAsColumnsRowTotals.push(rowTotals);
      });
      this.datesAsRowsTotals = Object.values(datesAsRowsTotals || {});
    }

    this.datesAsColumnsTotalOfTotals = [];
    this.datesAsColumnsRowTotals.forEach(rowTotal => {
      rowTotal.forEach((metric, index) => {
        if (this.datesAsColumnsTotalOfTotals[index]) {
          this.datesAsColumnsTotalOfTotals[index]['value'] += metric['value'] || 0;
        } else {
          this.datesAsColumnsTotalOfTotals[index] = { value: metric['value'] || 0, format: metric['format'], hide: metric['hide'] };
        }
      });
    });

    this.datesAsRowsTotalsByMetric = datesAsRowsTotals;
    this.datesAsColumnsColTotalsByMetric = chunk(this.datesAsColumnsColTotals, this.uniqueDates?.length || 1);
  }

  private hasNewChanges(previousValue, newValue): boolean {
    return JSON.stringify(previousValue) !== JSON.stringify(newValue);
  }

  private resetToFirstPage(): void {
    this.updateResult = true;
    this.updateCurrentPageNum(0);
  }

  private getRowsData(resultRows: Row[], excludeFalsy: boolean): Row[] {
    return resultRows.filter(row => excludeFalsy ? row.metrics.some(metric => !!metric.value) : true);
  }

  private getSlicesData(excludeFalsy: boolean) {
    const resultData = this.result.rows.filter(row => excludeFalsy ? row.metrics.some(metric => !!metric.value) : true);
    const data = this.removeDuplicatedSlices(resultData.map(row => row.slices));
    return chunk(data, this.resultsPerPage).map(group => group.filter(item => item !== null));
  }

  private updateSlicesData() {
    if (this._excludeFalsy) {
      this.uniqueSlices = this._filteredUniqueSlices;
    } else {
      this.uniqueSlices = this._uniqueSlices;
    }
  }

  private updateSeriesData() {
    if (this._excludeFalsy) {
      this.pages = this._filteredPages;
    } else {
      this.pages = this._pages;
    }
  }

  private updateResultSeriesData() {
    if (this._excludeFalsy) {
      this.resultSeries = this._filteredResultData;
    } else {
      this.resultSeries = this._resultData;
    }
  }

  private getResultsSeriesData(resultSeries, excludeFalsy): Series[] {
    return resultSeries.filter((serie: Series) =>
      excludeFalsy ? serie.metrics.some(metric => metric.values.some(value => !!value)) : true,
    );
  }

  conditionalColor(index, metric) {
    if (!this.chartOptions || !this.chartOptions.heatmap) {
      return;
    }

    const max = this.maxValues[metric.name];
    if (max !== null) {
      let metricValue;
      if (this.chartOptions.transpose === 'rows') {
        metricValue = metric.value;
      } else {
        metricValue = metric.values[index];
      }
      if (!metricValue) {
        return '#ffffff';
      }
      let v = metricValue / max;
      if (this.getComparison(metric) === 'red_increase') {
        v = 1.0 - v;
      }
      const color = this.colorScale(v);
      return color;
    }
  }

  getComparison(metric): MetricComparison {
    if (
      metric.metric &&
      metric.metric.meta &&
      metric.metric.meta.display &&
      metric.metric.meta.display.format &&
      metric.metric.meta.display.format.comparison
    ) {
      return metric.metric.meta.display.format.comparison;
    }
    return 'green_increase';
  }

  getBenchmarkClassNames(comparison: MetricComparison, value: number) {
    if (value === 0) {
      return '';
    }
    if ((value > 0 && comparison === 'green_increase') || (value < 0 && comparison === 'red_increase') ) {
      return 'benchmark__green';
    }
    return 'benchmark__red';
  }

  public comparisonDateTooltip(comparisonDate: string): string {
    return comparisonDate ? `Compared to ${comparisonDate}` : undefined;
  }

  updateCurrentPageNum(pageNum: number) {
    if (this.currentPageNum !== pageNum) {
      this.currentPageNum = pageNum;
      this.chartOptions.initialPageNum = this.currentPageNum;
      this.calculateGrandTotals();
      this.calculateBenchmarkPageTotals();
      this.changeDetectorRef.markForCheck();
    }
  }

  benchmarkDifference(rowIndex: number, metricName: string, value: number): number {
    const benchmarkValue = this.benchmarkValuePages?.[this.currentPageNum]?.[rowIndex]?.[metricName];

    if (value !== null && benchmarkValue > 0) {
      return value - benchmarkValue;
    }
  }

  totalBenchmark(value: number, benchmarkValue: number): number {
    return this.benchmarkService.diff(value ?? null, benchmarkValue);
  }

  totalDifference(value: number, benchmarkValue: number): number {
    if (value !== null && value !== undefined && benchmarkValue > 0) {
      return value - benchmarkValue;
    }
  }

  private calculateBenchmarkPageTotals(): void {
    const totals = {};
    const pageBenchmarks = this.benchmarkValuePages?.[this.currentPageNum] || [];
    pageBenchmarks.forEach(benchmarkRow => {
      Object.entries(benchmarkRow).forEach(([metricName, value]) => {
        totals[metricName] = (totals[metricName] || 0) + (Number(value) || 0);
      });
    });
    this.benchmarkPageTotals = totals;
  }

  removeDuplicatedSlices(items) {
    const unique = [];
    const uniqueItems = items.filter(item => {
      const itemValues = item.map(i => i._value);
      const isDuplicate = unique.some(uniqueItem => isEqual(uniqueItem, itemValues));
      if (!isDuplicate) {
        unique.push(itemValues);
        return true;
      }
      return false;
    });
    return uniqueItems;
  }

  getNbrOfPages() {
    if (this.chartOptions.transpose === 'columns') {
      return this.uniqueSlices.length;
    } else {
      return this.pages.length;
    }
  }

  private updateBenchmarkPages(benchmarkResults) {
    from(benchmarkResults).subscribe((b: Result) => {
      this.benchmarkTotals = <{ [metricName: string]: MetricValue }> b.totals;
      if (this.chartOptions.transpose === 'columns') {
        const benchSeriesData = this.getResultsSeriesData(b.series, this._excludeFalsy);
        const benchmarksBySlices = benchSeriesData.reduce((memo, series) => {
          const key = series.slices.map(s => s.id).join('-');
          const values = series.metrics.reduce((memo, metric) => {
            memo[metric.name] = metric.values;
            return memo;
          }, {});
          memo[key] = values;
          return memo;
        }, {});
        const benchmarks = this.resultSeries.map(series => {
          const key = series.slices.map(s => s.id).join('-');
          return benchmarksBySlices[key];
        });

        if (benchmarks && benchmarks.length) {
          this.benchmarkPages = this.benchmarkService.createBenchmarkPages(this.resultSeries, benchmarks, this.resultsPerPage, 'columns');
          this.comparisonDates = b.rows.slice(0, b.timestamps.length)
            .map(row => formatInTimeZone(row.date, 'UTC', this.dateFormat.pattern));
          this.loadingBenchmarks = false;
          this.changeDetectorRef.markForCheck();
        }
      } else {
        const rowsData = this.getRowsData(this.result.rows, this._excludeFalsy);
        const benchmarksBySlices = b.rows.reduce((memo, row) => {
          const key = row.slices.map(s => s.id).join('-');
          if (!memo[key]) {
            memo[key] = {};
          }
          const date = row.date.getTime();
          const values = row.metrics.reduce((memo, metric) => {
            memo[metric.name] = metric.value;
            return memo;
          }, {});
          memo[key][date] = values;
          return memo;
        }, {});

        const resultDates = this.benchmarkService.getSortedUniqueDatesFromRows(rowsData);
        const benchmarkDates = this.benchmarkService.getSortedUniqueDatesFromRows(b.rows);
        const comparisonDates = rowsData.map(row => benchmarkDates[resultDates.indexOf(row.date.getTime().toString())]);
        const benchmarks = rowsData.map((row, index) => {
          const key = row.slices.map(s => s.id).join('-');
          return benchmarksBySlices[key]?.[comparisonDates[index]];
        });

        if (benchmarks && benchmarks.length) {
          this.benchmarkPages = this.benchmarkService.createBenchmarkPages(rowsData, benchmarks, this.resultsPerPage);
          this.benchmarkValuePages = chunk(benchmarks.map(benchmark => benchmark || {}), this.resultsPerPage);
          this.calculateBenchmarkPageTotals();
          this.comparisonDatePages = this.result.query.rollup.value === 'total' ?
            undefined :
            chunk(comparisonDates.map(date => date && formatInTimeZone(new Date(Number(date)), 'UTC', this.dateFormat.pattern)), this.resultsPerPage);
          this.loadingBenchmarks = false;
          this.changeDetectorRef.markForCheck();
        }
      }
    });
  }

  private runQuery(query: Query) {
    return this.widgetQueueProxy
      .analiticsQueueProxy(query, { cache: true, skipLoader: true }).pipe(
        map(qr => Result.parse(qr)),
      );
  }

  private intervalFromDates(dates) {
    return this.datesService.fixedQueryInterval(this.datesService.parseValue(dates, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined));
  }

  private dateRangeLabel(startDate: Date, endDate: Date): string {
    return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
  }

  private getBenchmarkDates(dates: DateRangeValue) {
    if (this.compareTo.value && this.compareTo.value !== 'none') {
      return this.benchmarkService.calculateBenchmark(dates, this.compareTo.value, this.compareTo.dateRange, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined);
    } else {
      return null;
    }
  }

  private addTracking(changes: SimpleChanges) {
    if (changes?.options) {
      const { currentValue, previousValue } = changes?.options;

      if (previousValue?.dataOptions && currentValue?.dataOptions) {
        if (previousValue.dataOptions.excludeFalsy !== currentValue.dataOptions.excludeFalsy) {
          this.trackingService.track('Table Widget: Exclude N/A and 0 values', { value: currentValue.dataOptions.excludeFalsy });
        }
        if (previousValue.dataOptions.showColumnGrandTotals !== undefined && previousValue.dataOptions.showColumnGrandTotals !== currentValue.dataOptions.showColumnGrandTotals) {
          this.trackingService.track('Table Widget: Show column grand totals', { value: currentValue.dataOptions.showColumnGrandTotals });
        }
        if (previousValue.dataOptions.showRowGrandTotals !== undefined && previousValue.dataOptions.showRowGrandTotals !== currentValue.dataOptions.showRowGrandTotals) {
          this.trackingService.track('Table Widget: Show row grand totals', { value: currentValue.dataOptions.showRowGrandTotals });
        }
        if (previousValue.dataOptions.showColumnAllPagesTotals !== undefined && previousValue.dataOptions.showColumnAllPagesTotals !== currentValue.dataOptions.showColumnAllPagesTotals) {
          this.trackingService.track('Table Widget: Show column all pages totals', { value: currentValue.dataOptions.showColumnAllPagesTotals });
        }
      }

      if (previousValue?.footer && currentValue?.footer) {
        if (previousValue?.footer?.dateRange !== undefined && previousValue?.footer?.dateRange !== currentValue?.footer?.dateRange) {
          this.trackingService.track('Table Widget: Footer DateRange', { value: currentValue.footer.dateRange });
        }

        if (previousValue?.footer?.datasetInfo !== currentValue?.footer?.datasetInfo) {
          this.trackingService.track('Table Widget: Footer Dataset Info', { value: currentValue.footer.datasetInfo });
        }

        if (previousValue?.footer?.comparisonDateRange !== currentValue?.footer?.comparisonDateRange) {
          this.trackingService.track('Table Widget: Footer Comparison DateRange', { value: currentValue.footer.comparisonDateRange });
        }
      }

      if (previousValue?.compareTo && currentValue?.compareTo) {
        if (previousValue?.compareTo.value !== currentValue?.compareTo.value) {
          this.trackingService.track('Table Widget: Compare To', { value: currentValue.compareTo.name, dateRange: currentValue.compareTo.dateRange });
        }
      }

      if (previousValue?.compareToDisplay && currentValue?.compareToDisplay) {
        if (previousValue.compareToDisplay.separateColumn !== currentValue.compareToDisplay.separateColumn) {
          this.trackingService.track('Table Widget: Show comparison values in a separate column', { value: currentValue.compareToDisplay.separateColumn });
        }
        if (previousValue.compareToDisplay.benchmarksAs?.value !== currentValue.compareToDisplay.benchmarksAs?.value) {
          this.trackingService.track('Table Widget: Show benchmarks as', { value: currentValue.compareToDisplay.benchmarksAs.name });
        }
      }
    }
  }

  private writeSelectedTableToClipboard(event: ClipboardEvent): void {
    if (!event?.clipboardData) {
      return;
    }

    const table = this.selectedTable();
    if (!table) {
      return;
    }

    event.preventDefault();
    event.clipboardData.setData('text/plain', TableWidgetComponent.toPlainText(table));
    event.clipboardData.setData('text/html', TableWidgetComponent.toHtml(table));
  }

  private selectedTable(): CopiedTable | null {
    const selection = document.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return null;
    }

    const container = selection.getRangeAt(0).commonAncestorContainer;
    const element = container.nodeType === Node.ELEMENT_NODE ? (container as Element) : container.parentElement;

    if (element?.closest('td, th')) {
      return null;
    }

    const table = element?.closest('table') ?? element?.querySelector('table');
    if (!table) {
      return null;
    }

    const rows = TableWidgetComponent.buildCopiedRows(table as HTMLTableElement, selection);
    if (!rows) {
      return null;
    }

    return { title: this.selectedTitle(selection), rows };
  }

  private selectedTitle(selection: Selection): string | null {
    const titleElement: HTMLElement = this.elementRef.nativeElement.querySelector('.title');
    if (!titleElement || !selection.containsNode(titleElement, true)) {
      return null;
    }

    return titleElement.textContent?.trim() || null;
  }

  private static buildCopiedRows(table: HTMLTableElement, selection: Selection): CopiedRow[] | null {
    const grid: string[][] = [];
    const selectedRows = new Set<number>();
    const columnsWithContent = new Set<number>();
    let firstColumn = Number.MAX_SAFE_INTEGER;
    let lastColumn = -1;

    Array.from(table.rows).forEach((row, rowIndex) => {
      grid[rowIndex] = grid[rowIndex] ?? [];
      let column = 0;

      Array.from(row.cells).forEach(cell => {
        while (grid[rowIndex][column] !== undefined) {
          column++;
        }

        const isSelected = selection.containsNode(cell, true);
        if (isSelected) {
          selectedRows.add(rowIndex);
          firstColumn = Math.min(firstColumn, column);
          lastColumn = Math.max(lastColumn, column + cell.colSpan - 1);
        }

        const isSpacer = cell.classList.contains('spacer');
        for (let spannedRow = 0; spannedRow < cell.rowSpan; spannedRow++) {
          for (let spannedColumn = 0; spannedColumn < cell.colSpan; spannedColumn++) {
            grid[rowIndex + spannedRow] = grid[rowIndex + spannedRow] ?? [];
            grid[rowIndex + spannedRow][column + spannedColumn] =
              spannedRow === 0 && spannedColumn === 0 && isSelected ? TableWidgetComponent.cellText(cell) : '';
            if (!isSpacer) {
              columnsWithContent.add(column + spannedColumn);
            }
          }
        }

        column += cell.colSpan;
      });
    });

    if (lastColumn < 0) {
      return null;
    }

    const columns = TableWidgetComponent.columnRange(firstColumn, lastColumn).filter(column => columnsWithContent.has(column));

    return Array.from(table.rows)
      .map((row, rowIndex) => ({ row, rowIndex }))
      .filter(({ rowIndex }) => selectedRows.has(rowIndex))
      .map(({ row, rowIndex }) => ({
        isHeader: row.parentElement?.tagName === 'THEAD',
        cells: columns.map(column => grid[rowIndex][column] ?? ''),
      }));
  }

  private static columnRange(firstColumn: number, lastColumn: number): number[] {
    return Array.from({ length: lastColumn - firstColumn + 1 }, (_, offset) => firstColumn + offset);
  }

  private static cellText(cell: HTMLTableCellElement): string {
    const withoutLineBreaks = cell.cloneNode(true) as HTMLElement;
    withoutLineBreaks.querySelectorAll('br').forEach(lineBreak => lineBreak.replaceWith(' '));

    return (withoutLineBreaks.textContent ?? '').replace(/\s+/g, ' ').trim();
  }

  private static toPlainText({ title, rows }: CopiedTable): string {
    const lines = rows.map(row => row.cells.join('\t'));

    return (title ? [title, ...lines] : lines).join('\n');
  }

  private static toHtml({ title, rows }: CopiedTable): string {
    const markup = rows
      .map(row => {
        const tag = row.isHeader ? 'th' : 'td';
        const cells = row.cells.map(cell => `<${tag}>${TableWidgetComponent.escapeHtml(cell)}</${tag}>`).join('');
        return `<tr>${cells}</tr>`;
      })
      .join('');
    const heading = title ? `<p>${TableWidgetComponent.escapeHtml(title)}</p>` : '';

    return `${heading}<table>${markup}</table>`;
  }

  private static escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}
